import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Target, Brain, ShieldCheck, CalendarCheck, TrendingUp, Building2 } from "lucide-react";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";

const HERO_BG = "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBzaGFwZXMlMjAzZHxlbnwwfHx8fDE3ODI3NDI0ODN8MA&ixlib=rb-4.1.0&q=85";
const T1 = "https://images.unsplash.com/photo-1675869940341-d495d49010b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxhbWJpdGlvdXMlMjB5b3VuZyUyMHByb2Zlc3Npb25hbCUyMHRlY2glMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODI3NDI0ODR8MA&ixlib=rb-4.1.0&q=85";
const T2 = "https://images.unsplash.com/photo-1752738372136-2602aaafdcb7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhbWJpdGlvdXMlMjB5b3VuZyUyMHByb2Zlc3Npb25hbCUyMHRlY2glMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODI3NDI0ODR8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Target, title: "Set any career goal", desc: "Pick a role and salary target. Adyap reverse-engineers the exact skills you need." },
  { icon: Brain, title: "AI skill-gap analysis", desc: "Upload your resume. Gemini maps what you have against what the role demands." },
  { icon: CalendarCheck, title: "Day-by-day roadmap", desc: "A precise daily plan with curated resources, time estimates and milestones." },
  { icon: ShieldCheck, title: "Verified progress", desc: "Proof-backed tasks and streak integrity. No bulk-completing the past." },
  { icon: MessageIcon, title: "AI mock interviews", desc: "Role-specific questions with instant, scored feedback from your AI coach." },
  { icon: TrendingUp, title: "Readiness score", desc: "A single number that tells you — and recruiters — how job-ready you really are." },
];

function MessageIcon(props) { return <Brain {...props} />; }

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-white overflow-x-hidden">
      {/* Nav */}
      <PublicHeader />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, #050505 95%)" }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 pt-24 pb-28 md:pt-36 md:pb-40">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-block text-xs tracking-wider uppercase font-semibold text-primary border border-primary/30 rounded-full px-3 py-1 mb-6">AI Career Coach + Recruiter Marketplace</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-extrabold leading-[1.05]">
              Stop guessing what to learn.<br /><span className="text-primary">Start training</span> for the job.
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
              Adyap builds a personalized, day-by-day roadmap for any career goal — then verifies your progress with a readiness score recruiters can actually trust.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="hero-cta" className="group inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-3.5 rounded-full font-medium transition-colors glow-blue">
                Build my roadmap <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 px-6 py-3.5 rounded-full font-medium transition-colors">
                I already have an account
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-4 text-sm text-zinc-400">
              <div className="flex -space-x-2">
                <img src={T1} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-background" />
                <img src={T2} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-background" />
                <div className="w-8 h-8 rounded-full bg-accent/30 border-2 border-background flex items-center justify-center text-xs">+</div>
              </div>
              Trusted by ambitious engineers building toward their next role
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="max-w-2xl mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold">A coach that tells you exactly what to do each day.</h2>
          <p className="text-zinc-400 mt-3 text-base">Human coaches cost $75–300 a session and still give you vague advice. Adyap gives you a verifiable plan.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="glass rounded-2xl p-6 hover:-translate-y-1 hover:border-white/20 transition-all">
              <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Recruiter teaser */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="glass rounded-3xl p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 bg-accent/20 rounded-full blur-3xl" />
          <div className="relative max-w-2xl">
            <Building2 className="w-8 h-8 text-accent mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-bold">Recruiters post exactly what they need.</h2>
            <p className="text-zinc-400 mt-3 leading-relaxed">Soon: companies define the real tech stack and must-have skills. Candidates train against actual requirements — and recruiters get pre-qualified people with verified readiness scores. The data on what actually gets people hired becomes the moat.</p>
            <span className="inline-block mt-5 text-xs tracking-wider uppercase font-semibold text-zinc-500">Marketplace — coming in Phase 2</span>
          </div>
        </div>
      </section>

      {/* Founder story */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="max-w-3xl">
          <p className="text-2xl sm:text-3xl font-heading tracking-tight leading-snug">
            "I'm the first user. IIT grad who wanted more, but no tool told me exactly what to do each day. So I built it."
          </p>
          <p className="text-zinc-500 mt-4 text-sm">— Founder, Adyap</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 pb-28">
        <div className="text-center glass rounded-3xl py-16 px-6">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-extrabold">Your roadmap is one upload away.</h2>
          <p className="text-zinc-400 mt-3">Set a goal, drop your resume, and get your day-1 plan in minutes.</p>
          <Link to="/signup" data-testid="footer-cta" className="inline-flex items-center gap-2 mt-7 bg-white text-black hover:bg-zinc-200 px-7 py-3.5 rounded-full font-medium transition-colors">
            Get started free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
